import { captureException } from '@sentry/react';
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';

import { ShopModel } from '@merchant-clients/shop';

import { useShopQuery } from '@frontend/data';
import { Loading } from '@frontend/views';

import { useUser } from './user-context';

export interface ShopContextType {
  shop: ShopModel;
  setShopId: (shopId: string) => void;
}

export const ShopContext = createContext<ShopContextType>(null!);

export const ShopProvider = (props: PropsWithChildren) => {
  const { user } = useUser();
  const [shopId, setShopId] = useState<string>(user.shops[0]!);

  const { data: shop } = useShopQuery({
    shopId,
    onError: (error) => {
      captureException(error, {
        level: 'error',
        extra: {
          message: `Failed to fetch shop ${shopId} for user ${user.id}`,
        },
      });
    },
  });

  if (!shop) {
    return <Loading />;
  }

  return (
    <ShopContext.Provider
      value={{
        shop,
        setShopId,
      }}
    >
      {props.children}
    </ShopContext.Provider>
  );
};

export const useShopContext = () => {
  return useContext(ShopContext);
};

export const SyncShopToActiveRoute = ({ children }: PropsWithChildren) => {
  const { shop, setShopId } = useShopContext();
  const { shopId } = useParams();

  useEffect(() => {
    if (shopId && shop.id !== shopId) {
      setShopId(shopId);
    }
  }, [shop, shopId, setShopId]);

  return children;
};
